import React from "react";
import { scaleTime } from "d3-scale";
import { format } from "d3-format";

// Styles
import "./charts.css";

// Charting
import { ChartCanvas, Chart, ZoomButtons } from "react-stockcharts";
import { CrossHairCursor, MouseCoordinateY } from "react-stockcharts/lib/coordinates";
import { CandlestickSeries, BarSeries, LineSeries, BollingerSeries } from 'react-stockcharts/lib/series';
import { XAxis, YAxis } from 'react-stockcharts/lib/axes';
import { fitWidth } from 'react-stockcharts/lib/helper';
import { ema, heikinAshi, sma, bollingerBand } from "react-stockcharts/lib/indicator";
import { OHLCTooltip, HoverTooltip } from "react-stockcharts/lib/tooltip";

/**********************************************************
 *  Variables
 **********************************************************/

    const smaVolume50 = sma()
    .id(3)
    .options({ windowSize: 50, sourcePath: "volume" })
    .merge((d, c) => { d.smaVolume50 = c; })
    .accessor(d => d.smaVolume50);

    const ema20 = ema()
    .id(0)
    .options({ windowSize: 20 })
    .merge((d, c) => { d.ema20 = c; })
    .accessor(d => d.ema20)
    .stroke("#e040fb")
    .fill("#e040fb");

    const ema50 = ema()
    .id(2)
    .options({ windowSize: 50 })
    .merge((d, c) => { d.ema50 = c; })
    .accessor(d => d.ema50)
    .stroke("#4fc3f7")
    .fill("#4fc3f7");

    const bb = bollingerBand()
    .merge((d, c) => {d.bb = c;})
    .accessor(d => d.bb);

    const ha = heikinAshi();

/**********************************************************
 *  Functions
 **********************************************************/

class CandleStickChart extends React.Component {

    constructor(props) {

        super(props);

        this.state = {

        };

        this.handleReset = this.handleReset.bind(this);
    }

    // Shows the content in tooltip
    tooltipContent(ys) {
        return ({ currentItem, xAccessor }) => {
            return {
                x: [],
                y: []
                .concat(ys.map(each => ({
                    label: each.label,
                    value: each.value(currentItem),
                    stroke: each.stroke,
                })))
                .filter(line => line.value)
            };
        };
    }

    // Resets the zoom
    handleReset() {
		this.setState({
			suffix: this.state.suffix + 1
		});
	}

/**********************************************************
 *  Render
 **********************************************************/

	render() {

        //console.log('CandleStickChart.render()');

        var { type, data, ratio, properties } = this.props;
        const xAccessor = d => d.date;

        var gridHeight = properties.height - properties.margin.top - properties.margin.bottom;
        var gridWidth = properties.width - properties.margin.left - properties.margin.right;

        properties.yGrid.innerTickSize = -1 * gridWidth;
        properties.xGrid.innerTickSize = -1 * gridHeight;

        // Heikin Ashi  
        var finalData = data;

        if(this.props.chartType === 'Heikin-Ashi') {
            finalData = ha(data);
        }

        // Overlays
        var ema20Line, ema50Line, bollingerBands = null;

        // EMA 20
        if(this.props.overlays.indexOf('EMA 20') > -1) {
            finalData = ema20(finalData);
            ema20Line = (<LineSeries yAccessor={ema20.accessor()} stroke={ema20.stroke()}/>);
        }

        // EMA 50
        if(this.props.overlays.indexOf('EMA 50') > -1) {
            finalData = ema50(finalData);
            ema50Line = (<LineSeries yAccessor={ema50.accessor()} stroke={ema50.stroke()}/>);
        }

        // Bollinger Bands
        if(this.props.overlays.indexOf('Bollinger') > -1) {
            finalData = bb(finalData);
            const bbStroke = {
                top: "rgba(136, 132, 216, 0.6)",
                middle: "rgba(136, 132, 216, 0.3)",
                bottom: "rgba(136, 132, 216, 0.6)",
            };
            bollingerBands = (<BollingerSeries yAccessor={d => d.bb}
                stroke={bbStroke}
                fill="rgba(0,0,0,0)" />);
        }
        
        
		return (
			<ChartCanvas ratio={ratio} width={properties.width} height={properties.height}
					margin={properties.margin} type={type}
					seriesName="Yety"
					data={finalData}
					xAccessor={xAccessor} xScale={scaleTime()}
					xExtents={[this.props.from, this.props.to]}
                    mouseMoveEvent={properties.mouseMoveEvent}
                    panEvent={properties.panEvent}
                    zoomEvent={properties.zoomEvent}
                    clamp={properties.clamp}
                    >

                    <Chart id={1} yExtents={[d => [d.high, d.low], ema20.accessor(), ema50.accessor()]}
                        padding={{ top: 25, bottom: 20, right: 50 }}>

                        <XAxis axisAt="bottom" orient="bottom" ticks={properties.ticks.x} {...properties.xGrid} />
                        <YAxis axisAt="right" orient="right" ticks={properties.ticks.y} {...properties.yGrid} />

                        <MouseCoordinateY at="right" orient="right" displayFormat={format(".2f")} {...properties.mouseCoordinate}/>

                        <CandlestickSeries {...properties.candlesAppearance} />

                        <OHLCTooltip forChart={1} origin={[properties.ohlcSettings.left,properties.ohlcSettings.top]} {...properties.ohlcSettings}/>

                        <ZoomButtons onReset={this.handleReset.bind(this)} strokeWidth={2} stroke={'#ffffff'} strokeOpacity={0.55} fillOpacity={0} textFill={'#ffffff'} size={[36,26]} rx={10} ry={25}/>

                        {ema20Line}

                        {ema50Line}

                        {bollingerBands}

                        <HoverTooltip
						tooltipContent={this.tooltipContent([])}
						fontSize={14} {...properties.hoverSettings} />

                        <CrossHairCursor {...properties.crossHairSettings}/>


                    </Chart>

                    <Chart id={2}
                            yExtents={[d => d.volume, smaVolume50.accessor()]}
                            height={properties.volumeHeight} origin={(w, h) => [0, h - this.props.properties.volumeHeight]}>
                        
                        <BarSeries yAccessor={d => d.volume} fill="rgba(135, 152, 178,0.2)" />
                
                    </Chart>
			</ChartCanvas>
		);
	}
}

/*CandleStickChart.propTypes = {
	data: React.PropTypes.array.isRequired,
	//width: React.PropTypes.number.isRequired,
	ratio: React.PropTypes.number.isRequired,
	type: React.PropTypes.oneOf(["svg", "hybrid"]).isRequired,
};*/

CandleStickChart.defaultProps = {
	type: "hybrid",
};
CandleStickChart = fitWidth(CandleStickChart);

export default CandleStickChart;


// WEBPACK FOOTER //
// ./src/Views/Charting/CandleStickChart.js